import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowRight,
  CreditCard,
  Plus,
  SkipForward,
  Sparkles,
  Loader2,
  Lock,
  Zap,
} from "lucide-react";
import { CardWatchLogo } from "@/components/CardWatchLogo";
import { useAuth } from "@/context/AuthContext";
import ManualCardDialog from "@/components/ManualCardDialog";
import PlaidLinkButton from "@/components/PlaidLinkButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OptionCard = ({
  icon: Icon,
  title,
  sub,
  onClick,
  cta,
  primary,
  loading,
  testid,
  disabled,
  badge,
}) => (
  <button
    onClick={onClick}
    disabled={loading || disabled}
    aria-disabled={disabled || undefined}
    data-testid={testid}
    className={`text-left rounded-2xl border p-6 transition-all duration-200 group disabled:cursor-not-allowed ${
      disabled
        ? "border-white/10 bg-white/[0.02] opacity-70"
        : primary
          ? "border-[#00E676]/40 bg-[#00E676]/[0.06] hover:bg-[#00E676]/[0.12] hover:border-[#00E676] hover:-translate-y-0.5"
          : "border-white/5 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.05] hover:-translate-y-0.5"
    }`}
  >
    <div className="flex items-center gap-2">
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center ${
          disabled
            ? "bg-white/[0.04] border border-white/10 text-slate-400"
            : primary
              ? "bg-[#00E676]/[0.14] border border-[#00E676]/40 text-[#00E676]"
              : "bg-white/[0.04] border border-white/10 text-slate-300"
        }`}
      >
        {loading ? <Loader2 size={17} className="animate-spin" /> : <Icon size={17} />}
      </div>
      {badge && (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-[#F5B841]/40 bg-[#F5B841]/[0.08] px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] text-[#F5B841] font-bold"
          data-testid={`${testid}-coming-soon-badge`}
        >
          <Lock size={9} /> {badge}
        </span>
      )}
    </div>
    <h3
      className="mt-4 text-white text-lg font-semibold tracking-tight"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {title}
    </h3>
    <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{sub}</p>
    {!disabled && (
      <div
        className={`mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${
          primary ? "text-[#00E676]" : "text-slate-400 group-hover:text-white"
        }`}
      >
        {cta} <ArrowRight size={12} />
      </div>
    )}
  </button>
);

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [manualOpen, setManualOpen] = useState(false);
  const [plaidEnabled, setPlaidEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/plaid/status`, { withCredentials: true })
      .then((res) => { if (!cancelled) setPlaidEnabled(Boolean(res.data?.enabled)); })
      .catch(() => { if (!cancelled) setPlaidEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  const complete = async (destination = "/dashboard") => {
    try {
      await axios.post(`${API}/auth/complete_onboarding`, {}, { withCredentials: true });
      await refresh();
    } catch { /* non-blocking */ }
    navigate(destination, { replace: true });
  };

  const onPlaidLinked = async () => {
    await complete("/dashboard");
  };

  const onManualSubmitted = async () => {
    setManualOpen(false);
    await complete("/dashboard");
  };

  return (
    <div className="min-h-screen bg-[#060B19] text-white relative overflow-hidden">
      <div className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/[0.08] blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/25 blur-[160px]" />

      <header className="relative z-10 border-b border-white/5">
        <div className="w-full max-w-5xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <CardWatchLogo size={30} />
          <button
            onClick={() => complete("/dashboard")}
            data-testid="onboarding-skip"
            className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <SkipForward size={13} /> Skip for now
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/30 bg-[#00E676]/[0.06] px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
            <Sparkles size={11} /> Welcome, {user?.name?.split(" ")[0] || "there"}
          </div>
          <h1
            className="mt-6 text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-2xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="onboarding-title"
          >
            Let's add your <span className="text-[#00E676]">first card</span>.
          </h1>
          <p className="mt-5 text-slate-300 text-[15px] leading-relaxed max-w-xl">
            Pick the way that suits you. You can always add more cards later — CardWatch
            supports unlimited cards on Pro and up to 2 on the free plan.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {plaidEnabled ? (
            <div
              data-testid="onboarding-link-plaid"
              className="text-left rounded-2xl border border-[#00E676]/40 bg-[#00E676]/[0.06] p-6"
            >
              <div className="flex items-center gap-2">
                <div className="w-11 h-11 rounded-xl bg-[#00E676]/[0.14] border border-[#00E676]/40 text-[#00E676] flex items-center justify-center">
                  <Zap size={17} />
                </div>
                <span className="inline-flex items-center gap-1 rounded-full border border-[#00E676]/40 bg-[#00E676]/[0.08] px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] text-[#00E676] font-bold">
                  Powered by Plaid
                </span>
              </div>
              <h3
                className="mt-4 text-white text-lg font-semibold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Automatic Sync
              </h3>
              <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">
                Bank-grade open banking. Read-only. Balances and transactions update automatically — UK, EU, US and Canada supported.
              </p>
              <div className="mt-5">
                <PlaidLinkButton
                  onLinked={onPlaidLinked}
                  label="Connect your bank"
                  testid="onboarding-plaid-open"
                />
              </div>
            </div>
          ) : (
            <OptionCard
              testid="onboarding-link-saltedge"
              icon={CreditCard}
              primary
              disabled
              badge="Coming soon"
              title="Automatic Sync"
              sub="Live balance + limit updates from your bank. We're finishing the last mile of production integration — check back soon."
              cta="Coming soon"
            />
          )}
          <OptionCard
            testid="onboarding-add-manual"
            icon={Plus}
            title="Add a card manually"
            sub="Type in nickname, balance, credit limit and due date. Updates instantly, no bank connection needed — and you'll still get due-date reminders."
            cta="Add manually"
            onClick={() => setManualOpen(true)}
          />
        </div>

        <p className="mt-6 text-[11px] text-slate-400 text-center max-w-xl mx-auto">
          {plaidEnabled
            ? "Your bank credentials never touch our servers. Plaid handles the connection."
            : "You can always switch to Automatic Sync later — nothing you enter now is lost."}
        </p>

        <div className="mt-10 text-center">
          <button
            onClick={() => complete("/dashboard")}
            data-testid="onboarding-continue-empty"
            className="text-sm text-slate-500 hover:text-white transition-colors underline underline-offset-4"
          >
            I'll set this up later — take me to my dashboard
          </button>
        </div>
      </main>

      <ManualCardDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onSubmitted={onManualSubmitted}
        userId={user?.user_id}
      />
    </div>
  );
};

export default Onboarding;
